<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Input from '@uiKit/elements/Input.vue'
import Message from '@uiKit/elements/Message.vue'
import Text from '@uiKit/elements/Text.vue'
import FormField from '@uiKit/components/FormField.vue'
import { StatementUploadInput } from '@modules/account'

interface Props {
  amount: string
  submitError?: string | null
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitError: null,
  submitting: false,
})

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'confirm', payload: { referenceId: string; attachment: File }): void
}>()

const referenceId = ref('')
const file = ref<File | null>(null)
const referenceError = ref('')
const attachmentError = ref('')

watch(referenceId, () => {
  if (referenceError.value) referenceError.value = ''
})

watch(file, () => {
  if (attachmentError.value) attachmentError.value = ''
})

const formError = computed(() => props.submitError ?? '')

const handleConfirm = () => {
  const trimmed = referenceId.value.trim()
  const attachment = file.value
  let valid = true
  if (!trimmed) {
    referenceError.value = 'Enter the bank reference or transaction ID.'
    valid = false
  }
  if (!attachment) {
    attachmentError.value = 'Upload a screenshot or receipt of your transfer.'
    valid = false
  }
  if (!valid || !attachment) return
  referenceError.value = ''
  attachmentError.value = ''
  emit('confirm', { referenceId: trimmed, attachment })
}
</script>

<template>
  <Box direction="col" gap="4" class="dcs">
    <Box direction="col" gap="4" class="dcs__body">
      <Box direction="col" gap="2" class="dcs__summary">
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Amount</Text>
          <Text type="body" weight="semibold" is="span">AED {{ amount }}</Text>
        </Box>
        <Box class="dcs__divider" />
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Status</Text>
          <Text type="body" weight="semibold" color="warning" is="span">Pending</Text>
        </Box>
      </Box>

      <FormField
        label="Bank reference / Transaction ID"
        required
        :error="referenceError"
        helper-text="You'll find this on your bank's confirmation screen."
      >
        <Input
          v-model="referenceId"
          name="bank-reference"
          placeholder="Paste the reference from your bank"
          :invalid="!!referenceError"
        />
      </FormField>

      <FormField
        label="Attachment"
        required
        :error="attachmentError"
        helper-text="Upload a screenshot or receipt of your bank transfer."
      >
        <StatementUploadInput v-model="file" prompt-text="Tap to upload receipt" />
      </FormField>

      <Message v-if="formError" color="danger" variant="outlined" :message="formError" />
    </Box>

    <Box class="dcs__spacer" />

    <Box direction="col" gap="3" class="dcs__actions">
      <AppButton
        label="Confirm Transfer"
        block
        size="large"
        :loading="submitting"
        @click="handleConfirm"
      />
      <AppButton label="Back" variant="text" color="secondary" block @click="emit('back')" />
    </Box>
  </Box>
</template>

<style scoped>
.dcs {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
}

.dcs__body {
  flex-shrink: 0;
}

.dcs__summary {
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  padding: 0.875rem 1rem;
}

.dcs__divider {
  height: 1px;
  background: var(--p-surface-200);
  margin: 0.25rem 0;
}

.dcs__spacer {
  flex: 1;
  min-height: 1rem;
}

.dcs__actions {
  flex-shrink: 0;
}
</style>
